<script setup lang="ts">
import { clamp } from "@vueuse/core";
import { computed } from "vue";

import { SizeFormat } from "~/shared/enums";

const X1 = 3;
const Y1 = 3;
const X2 = 21;
const Y2 = 21;
const SPAN = X2 - X1;

const SQRT_5 = Math.sqrt(5);
const PHI = (1 + SQRT_5) / 2;
const LOG_PHI = Math.log(PHI);
const LOG_2 = Math.log(2);

const MAX_FIBONACCI = 8;
const MAX_LINEAR = 5;
const MAX_EXPONENTIAL = 16;
const MAX_FREE_ENTRY = 8;

const N_MAX_FIB = Math.log(MAX_FIBONACCI * SQRT_5) / LOG_PHI;
const N_MAX_EXP = Math.log(MAX_EXPONENTIAL) / LOG_2;

const FILL_MIN = 0.2;
const FILL_MAX = 0.85;

const OUTLINE_PATH = `M ${X1} ${Y2} L ${X2} ${Y2} L ${X2} ${Y1} Z`;

const props = withDefaults(
  defineProps<{
    size?: number;
    format?: SizeFormat;
  }>(),
  {
    size: 0,
    format: SizeFormat.FIBONACCI,
  }
);

const normalize = (x: number): number => {
  if (x <= 0) {
    return 0;
  }
  switch (props.format) {
    case SizeFormat.TSHIRT:
    case SizeFormat.FIBONACCI: {
      return Math.log(x * SQRT_5) / LOG_PHI / N_MAX_FIB;
    }
    case SizeFormat.EXPONENTIAL: {
      return Math.log(x) / LOG_2 / N_MAX_EXP;
    }
    case SizeFormat.LINEAR: {
      return x / MAX_LINEAR;
    }
    case SizeFormat.FREE_ENTRY: {
      return x / MAX_FREE_ENTRY;
    }
    default: {
      throw new Error(`Unknown size format: ${props.format}`);
    }
  }
};

const fillProportion = computed(() => {
  const raw = clamp(normalize(props.size), 0, 1);
  if (raw === 0) {
    return 0;
  }
  return FILL_MIN + raw * (FILL_MAX - FILL_MIN);
});

const fillPath = computed(() => {
  const p = fillProportion.value;
  if (p === 0) {
    return "";
  }
  const xFill = X1 + p * SPAN;
  const yFill = Y2 - p * SPAN;
  return `M ${X1} ${Y2} L ${xFill} ${Y2} L ${xFill} ${yFill} Z`;
});
</script>

<template>
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path v-if="fillProportion > 0" :d="fillPath" fill="currentColor" />
    <path
      :d="OUTLINE_PATH"
      stroke="currentColor"
      stroke-width="2"
      stroke-linejoin="round"
      stroke-linecap="round"
      fill="none" />
  </svg>
</template>
